/**
 * =============================================================================
 * ADMIN IMAGE UPLOADER (ADR-022 §10(A) — PR4b, BL-IMG-ADMIN-AUTHORING-001)
 * =============================================================================
 *
 * Minimal ADMIN-NATIVE staging tray for staff image authoring: pick files,
 * see a per-file status list (pending / uploading / done / failed + reason),
 * remove a file before upload, upload sequentially. Deliberately NOT the
 * storefront widget uploader — no client-side compression, no drag-and-drop,
 * no previews: the server chokepoint (validate → magic bytes → dimension
 * bound → EXIF strip) is authoritative for every author (ADR-022 §5 — trust
 * applies to moderation, never the strip), and the widget's PR5a compression
 * pipeline is a customer-bandwidth optimization out of scope here.
 *
 * Client pre-validation is UX ONLY and runs against the EFFECTIVE limits the
 * parent passes (§10.1-resolved settings where the parent has them, or the
 * FILE_VALIDATION platform defaults): declared MIME type ∈ allowedFormats,
 * size ≤ maxSizeMb, ≤ maxFiles selected. A file that slips past (spoofed
 * type, tenant-tightened server cap) fails server-side and surfaces as a
 * per-file failure reason.
 *
 * Uploads are SEQUENTIAL behind a synchronous batch guard: the upload POST is
 * non-idempotent and never retried (client layer maxRetries: 0), so exactly
 * one request per file is ever in flight.
 *
 * =============================================================================
 */

import React, { useCallback, useRef, useState } from 'react';
import { Box, Button, Flex, FlexItem, Text } from '@bigcommerce/big-design';
import { tokens } from '../../tokens';

/** Lifecycle of one staged file. */
type StagedFileStatus = 'pending' | 'uploading' | 'done' | 'failed';

/** One row of the staging tray. */
interface StagedFile {
  /** Render key only — never sent. */
  key: number;
  file: File;
  status: StagedFileStatus;
  /** Failure reason (client validation or the server's per-file message). */
  reason?: string;
}

/**
 * Props for AdminImageUploader.
 */
export interface AdminImageUploaderProps {
  /** Effective per-target image cap (UX gate only — server authoritative). */
  maxFiles: number;
  /** Effective per-file size cap in MB (UX gate only — server authoritative). */
  maxSizeMb: number;
  /** Effective allowed MIME types (UX gate only — server authoritative). */
  allowedFormats: readonly string[];
  /**
   * Upload ONE file. Resolves the per-file outcome; a `message` on failure is
   * rendered inline against the file row. Called sequentially — never
   * concurrently.
   */
  onUpload: (file: File) => Promise<{ success: boolean; message?: string }>;
  /** Disables the picker and all actions (e.g. token missing). */
  disabled?: boolean;
  /** Accessible label for the file input (default "Add images"). */
  label?: string;
  /** Optional test ID prefix. */
  testId?: string;
}

/** Human label for a failed client validation, stating the effective bound. */
function validateFile(
  file: File,
  maxSizeMb: number,
  allowedFormats: readonly string[]
): string | null {
  if (!allowedFormats.includes(file.type)) {
    return 'Unsupported format.';
  }
  if (file.size > maxSizeMb * 1024 * 1024) {
    return `File is too large (max ${maxSizeMb} MB).`;
  }
  return null;
}

const STATUS_LABELS: Record<StagedFileStatus, string> = {
  pending: 'Ready',
  uploading: 'Uploading…',
  done: 'Uploaded',
  failed: 'Failed',
};

/**
 * Minimal staging tray for staff image uploads.
 */
export function AdminImageUploader({
  maxFiles,
  maxSizeMb,
  allowedFormats,
  onUpload,
  disabled = false,
  label = 'Add images',
  testId,
}: AdminImageUploaderProps): React.ReactElement {
  const [staged, setStaged] = useState<StagedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const keyCounterRef = useRef(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Synchronous batch guard (useReplies precedent): two same-tick Upload
  // clicks must start exactly one sequential run — state alone is async.
  const isUploadingRef = useRef(false);

  const handleSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const files = Array.from(event.target.files ?? []);
      if (files.length === 0) return;
      setStaged((prev) => {
        // Slots already claimed by anything not failed (done rows count —
        // they consumed a server-side cap slot this session).
        let taken = prev.filter((f) => f.status !== 'failed').length;
        const next = [...prev];
        for (const file of files) {
          const key = keyCounterRef.current++;
          const reason = validateFile(file, maxSizeMb, allowedFormats);
          if (reason) {
            next.push({ key, file, status: 'failed', reason });
            continue;
          }
          if (taken >= maxFiles) {
            next.push({
              key,
              file,
              status: 'failed',
              reason: `Limit is ${maxFiles} image${maxFiles === 1 ? '' : 's'}.`,
            });
            continue;
          }
          taken += 1;
          next.push({ key, file, status: 'pending' });
        }
        return next;
      });
      // Allow re-selecting the same file after a remove (change events only
      // fire when the input value differs).
      event.target.value = '';
    },
    [maxSizeMb, allowedFormats, maxFiles]
  );

  const removeStaged = useCallback((key: number): void => {
    // Remove-before-upload only: uploading/done rows are no longer removable
    // here (removal of PERSISTED media is the management surface's job).
    setStaged((prev) =>
      prev.filter((f) => f.key !== key || f.status === 'uploading' || f.status === 'done')
    );
  }, []);

  const handleUpload = useCallback(async (): Promise<void> => {
    if (isUploadingRef.current || disabled) return;
    isUploadingRef.current = true;
    setIsUploading(true);
    try {
      // Snapshot the pending keys once — files staged mid-run wait for the
      // next click (keeps the sequential loop deterministic).
      const pendingKeys = staged.filter((f) => f.status === 'pending').map((f) => f.key);
      for (const key of pendingKeys) {
        const entry = staged.find((f) => f.key === key);
        if (!entry) continue;
        setStaged((prev) =>
          prev.map((f) => (f.key === key ? { ...f, status: 'uploading' as const } : f))
        );
        try {
          const outcome = await onUpload(entry.file);
          setStaged((prev) =>
            prev.map((f) =>
              f.key === key
                ? outcome.success
                  ? { ...f, status: 'done' as const, reason: undefined }
                  : {
                      ...f,
                      status: 'failed' as const,
                      reason: outcome.message ?? 'Upload failed.',
                    }
                : f
            )
          );
        } catch {
          // The onUpload wrappers are contractually non-throwing; guard so a
          // rejection can neither wedge the run nor leave a row "uploading".
          setStaged((prev) =>
            prev.map((f) =>
              f.key === key ? { ...f, status: 'failed' as const, reason: 'Upload failed.' } : f
            )
          );
        }
      }
    } finally {
      isUploadingRef.current = false;
      setIsUploading(false);
    }
  }, [staged, onUpload, disabled]);

  const pendingCount = staged.filter((f) => f.status === 'pending').length;
  const canUpload = pendingCount > 0 && !isUploading && !disabled;

  return (
    <Box data-testid={testId}>
      <Flex flexGap="0.5rem" alignItems="center" flexWrap="wrap">
        <FlexItem>
          <Button
            variant="secondary"
            disabled={disabled || isUploading}
            onClick={() => inputRef.current?.click()}
            data-testid={testId ? `${testId}-pick` : undefined}
          >
            {label}
          </Button>
          {/* Hidden native input triggered by the button; keeps the visible
              control a real BigDesign button while remaining programmatically
              labelled and reachable for tests/AT. */}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            disabled={disabled || isUploading}
            onChange={handleSelect}
            aria-label={label}
            style={{ display: 'none' }}
            data-testid={testId ? `${testId}-input` : undefined}
          />
        </FlexItem>
        {pendingCount > 0 && (
          <FlexItem>
            <Button
              variant="primary"
              disabled={!canUpload}
              isLoading={isUploading}
              onClick={() => void handleUpload()}
              data-testid={testId ? `${testId}-upload` : undefined}
            >
              Upload {pendingCount} image{pendingCount === 1 ? '' : 's'}
            </Button>
          </FlexItem>
        )}
      </Flex>

      <Text color="secondary">
        Up to {maxFiles} image{maxFiles === 1 ? '' : 's'}, {maxSizeMb} MB each.
      </Text>

      {staged.length > 0 && (
        <Box
          role="list"
          aria-label="Selected images"
          marginTop="xSmall"
          data-testid={testId ? `${testId}-list` : undefined}
        >
          {staged.map((entry) => (
            <Flex
              key={entry.key}
              role="listitem"
              alignItems="center"
              flexGap="0.5rem"
              flexWrap="wrap"
              style={{ padding: `${tokens.dimension.space.xxs} 0` }}
              data-testid={testId ? `${testId}-file-${entry.key}` : undefined}
            >
              <FlexItem style={{ minWidth: 0 }}>
                <Text
                >
                  {entry.file.name}
                </Text>
              </FlexItem>
              <FlexItem>
                <Text
                  data-testid={testId ? `${testId}-file-${entry.key}-status` : undefined}
                >
                  {STATUS_LABELS[entry.status]}
                  {entry.status === 'failed' && entry.reason ? ` — ${entry.reason}` : ''}
                </Text>
              </FlexItem>
              {(entry.status === 'pending' || entry.status === 'failed') && (
                <FlexItem>
                  <Button
                    variant="subtle"
                    disabled={disabled || isUploading}
                    onClick={() => removeStaged(entry.key)}
                    aria-label={`Remove ${entry.file.name}`}
                    data-testid={testId ? `${testId}-file-${entry.key}-remove` : undefined}
                  >
                    Remove
                  </Button>
                </FlexItem>
              )}
            </Flex>
          ))}
        </Box>
      )}
    </Box>
  );
}
